import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Chip,
    Alert,
    CircularProgress,
    Breadcrumbs,
    Grid,
    FormControlLabel,
    Checkbox,
    Autocomplete,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Tabs,
    Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { useActiveMemberProfiles } from '../../hooks/useActiveMemberProfiles';
import { personalTrainingAPI, personalTrainingTypeAPI, userAPI, memberProfileAPI, personalTrainingPackAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatTimeString } from '../../utils/dateUtils';

interface PTSession {
    id: string;
    name: string;
    description?: string;
    coachId: string;
    instructorName: string;
    typeId: string;
    type?: { id: string; title: string; defaultPrice: number };
    venue: string;
    sessionDate: string;
    startTime: string;
    endTime: string;
    priceOverride?: number;
    status: string;
    enrollments?: any[];
    notes?: string;
}

interface PTType {
    id: string;
    title: string;
    defaultPrice: number;
}

interface Coach {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
}

interface MemberProfile {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    status?: string;
}

const PersonalTrainingsPage: React.FC = () => {
    const { user } = useAuth();
    const [sessions, setSessions] = useState<PTSession[]>([]);
    const [filteredSessions, setFilteredSessions] = useState<PTSession[]>([]);
    const [ptTypes, setPTTypes] = useState<PTType[]>([]);
    const [coaches, setCoaches] = useState<Coach[]>([]);
    
    // Fetch active member profiles with deduplication
    const { profiles: memberProfiles, loading: loadingMemberProfiles } = useActiveMemberProfiles({ deduplicate: true });
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Dialogs
    const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
    const [enrollmentDialogOpen, setEnrollmentDialogOpen] = useState(false);
    const [editingSession, setEditingSession] = useState<PTSession | null>(null);
    const [selectedSession, setSelectedSession] = useState<PTSession | null>(null);

    // Filters
    const [filterCoach, setFilterCoach] = useState<string>(user?.role === 'coach' ? user.id : '');
    const [filterDateStart, setFilterDateStart] = useState<string>('');
    const [filterDateEnd, setFilterDateEnd] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [tabValue, setTabValue] = useState(0);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [sessions, filterCoach, filterDateStart, filterDateEnd, filterStatus, tabValue]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [sessionsRes, typesRes, coachesRes] = await Promise.all([
                personalTrainingAPI.getAll(),
                personalTrainingTypeAPI.getAll(),
                userAPI.getCoaches()
            ]);

            setSessions(sessionsRes.data);
            setPTTypes(typesRes.data);
            setCoaches(coachesRes.data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...sessions];

        // Tab filter (upcoming vs past)
        const today = new Date().toISOString().split('T')[0];
        if (tabValue === 0) {
            // Upcoming
            filtered = filtered.filter(s => s.sessionDate >= today);
        } else {
            // Past
            filtered = filtered.filter(s => s.sessionDate < today);
        }

        if (filterCoach) {
            filtered = filtered.filter(s => s.coachId === filterCoach);
        }
        if (filterDateStart) {
            filtered = filtered.filter(s => s.sessionDate >= filterDateStart);
        }
        if (filterDateEnd) {
            filtered = filtered.filter(s => s.sessionDate <= filterDateEnd);
        }
        if (filterStatus) {
            filtered = filtered.filter(s => s.status === filterStatus);
        }

        setFilteredSessions(filtered);
    };

    const handleSaveSession = async (data: any) => {
        try {
            if (editingSession) {
                await personalTrainingAPI.update(editingSession.id, data);
                setSuccessMessage('PT session updated successfully');
            } else {
                await personalTrainingAPI.create(data);
                setSuccessMessage('PT session created successfully');
            }
            await fetchData();
            setSessionDialogOpen(false);
            setEditingSession(null);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save PT session');
        }
    };

    const handleDeleteSession = async (sessionId: string) => {
        if (window.confirm('Are you sure you want to delete this PT session?')) {
            try {
                await personalTrainingAPI.delete(sessionId);
                setSuccessMessage('PT session deleted successfully');
                await fetchData();
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to delete PT session');
            }
        }
    };

    const handleEnrollMember = async (sessionId: string, memberProfileId: string, notes?: string) => {
        try {
            await personalTrainingAPI.enrollMember(sessionId, { memberProfileId, notes });
            setSuccessMessage('Member enrolled successfully');
            await fetchData();
            setEnrollmentDialogOpen(false);
            return { success: true };
        } catch (err: any) {
            return { success: false, error: err.response?.data?.message || 'Failed to enroll member' };
        }
    };

    const handleCancelEnrollment = async (sessionId: string, enrollmentId: string) => {
        if (window.confirm('Cancel this enrollment?')) {
            try {
                await personalTrainingAPI.cancelEnrollment(sessionId, enrollmentId);
                setSuccessMessage('Enrollment cancelled successfully');
                await fetchData();
                
                // Update the selected session with fresh data
                if (selectedSession && selectedSession.id === sessionId) {
                    const response = await personalTrainingAPI.getAll();
                    const updatedSession = response.data.find((s: PTSession) => s.id === sessionId);
                    if (updatedSession) {
                        setSelectedSession(updatedSession);
                    }
                }
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to cancel enrollment');
            }
        }
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'scheduled': return 'primary';
            case 'completed': return 'success';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
                <RouterLink to="/dashboard" style={{ display: 'flex', alignItems: 'center' }}>
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Home
                </RouterLink>
                <Typography sx={{ display: 'flex', alignItems: 'center' }} color="text.primary">
                    <FitnessCenterIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Personal Training
                </Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Personal Training Sessions</Typography>
                {(user?.role === 'admin' || user?.role === 'coach') && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setEditingSession(null);
                            setSessionDialogOpen(true);
                        }}
                    >
                        New Session
                    </Button>
                )}
            </Box>

            {/* Error/Success Messages */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            select
                            fullWidth
                            label="Coach"
                            value={filterCoach}
                            onChange={(e) => setFilterCoach(e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">All Coaches</MenuItem>
                            {coaches.map((coach) => (
                                <MenuItem key={coach.id} value={coach.id}>
                                    {coach.firstName} {coach.lastName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            type="date"
                            fullWidth
                            label="Start Date"
                            value={filterDateStart}
                            onChange={(e) => setFilterDateStart(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            type="date"
                            fullWidth
                            label="End Date"
                            value={filterDateEnd}
                            onChange={(e) => setFilterDateEnd(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            select
                            fullWidth
                            label="Status"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                            <MenuItem value="COMPLETED">Completed</MenuItem>
                            <MenuItem value="CANCELLED">Cancelled</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            {/* Tabs */}
            <Tabs value={tabValue} onChange={(_, val) => setTabValue(val)} sx={{ mb: 2 }}>
                <Tab label="Upcoming Sessions" />
                <Tab label="Past Sessions" />
            </Tabs>

            {/* Sessions Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Time</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Coach</TableCell>
                            <TableCell>Venue</TableCell>
                            <TableCell>Enrolled</TableCell>
                            <TableCell>Price</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredSessions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    <Typography color="text.secondary">No sessions found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredSessions.map((session) => (
                                <TableRow key={session.id}>
                                    <TableCell>{formatDate(session.sessionDate)}</TableCell>
                                    <TableCell>
                                        {formatTimeString(session.startTime)} - {formatTimeString(session.endTime)}
                                    </TableCell>
                                    <TableCell>{session.name}</TableCell>
                                    <TableCell>{session.type?.title || 'N/A'}</TableCell>
                                    <TableCell>{session.instructorName}</TableCell>
                                    <TableCell>{session.venue}</TableCell>
                                    <TableCell>{session.enrollments?.filter(e => e.status === 'enrolled').length || 0}</TableCell>
                                    <TableCell>
                                        ${(session.priceOverride ?? session.type?.defaultPrice ?? 0)}
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={session.status} color={getStatusColor(session.status)} size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        {(user?.role === 'admin' || user?.role === 'coach') && (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedSession(session);
                                                        setEnrollmentDialogOpen(true);
                                                    }}
                                                    title="Manage Enrollments"
                                                >
                                                    <GroupAddIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setEditingSession(session);
                                                        setSessionDialogOpen(true);
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteSession(session.id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Session Create/Edit Dialog */}
            <PTSessionDialog
                open={sessionDialogOpen}
                onClose={() => {
                    setSessionDialogOpen(false);
                    setEditingSession(null);
                }}
                onSave={handleSaveSession}
                session={editingSession}
                ptTypes={ptTypes}
                coaches={coaches}
            />

            {/* Enrollment Dialog */}
            <EnrollmentDialog
                open={enrollmentDialogOpen}
                onClose={() => {
                    setEnrollmentDialogOpen(false);
                    setSelectedSession(null);
                }}
                session={selectedSession}
                memberProfiles={memberProfiles}
                onEnroll={handleEnrollMember}
                onCancel={handleCancelEnrollment}
            />
        </Container>
    );
};

// PT Session Dialog Component
interface PTSessionDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    session: PTSession | null;
    ptTypes: PTType[];
    coaches: Coach[];
}

const PTSessionDialog: React.FC<PTSessionDialogProps> = ({ open, onClose, onSave, session, ptTypes, coaches }) => {
    const [formData, setFormData] = useState<any>({
        name: '',
        description: '',
        coachId: '',
        typeId: '',
        venue: '',
        sessionDate: '',
        startTime: '09:00',
        endTime: '10:00',
        priceOverride: '',
        notes: ''
    });

    useEffect(() => {
        if (session) {
            setFormData({
                name: session.name,
                description: session.description || '',
                coachId: session.coachId,
                typeId: session.typeId,
                venue: session.venue,
                sessionDate: session.sessionDate,
                startTime: session.startTime.substring(0, 5),
                endTime: session.endTime.substring(0, 5),
                priceOverride: session.priceOverride || '',
                notes: session.notes || ''
            });
        } else {
            setFormData({
                name: '',
                description: '',
                coachId: '',
                typeId: '',
                venue: '',
                sessionDate: '',
                startTime: '09:00',
                endTime: '10:00',
                priceOverride: '',
                notes: ''
            });
        }
    }, [session, open]);

    const handleSubmit = () => {
        const data = { ...formData };
        if (data.priceOverride === '') {
            delete data.priceOverride;
        }
        onSave(data);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{session ? 'Edit PT Session' : 'Create PT Session'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Session Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            multiline
                            rows={2}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            select
                            fullWidth
                            label="Coach"
                            value={formData.coachId}
                            onChange={(e) => setFormData({ ...formData, coachId: e.target.value })}
                            required
                        >
                            {coaches.map((coach) => (
                                <MenuItem key={coach.id} value={coach.id}>
                                    {coach.firstName} {coach.lastName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            select
                            fullWidth
                            label="PT Type"
                            value={formData.typeId}
                            onChange={(e) => setFormData({ ...formData, typeId: e.target.value })}
                            required
                        >
                            {ptTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.title} (${type.defaultPrice})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label="Venue"
                            value={formData.venue}
                            onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            type="date"
                            fullWidth
                            label="Session Date"
                            value={formData.sessionDate}
                            onChange={(e) => setFormData({ ...formData, sessionDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            type="time"
                            fullWidth
                            label="Start Time"
                            value={formData.startTime}
                            onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            type="time"
                            fullWidth
                            label="End Time"
                            value={formData.endTime}
                            onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            type="number"
                            fullWidth
                            label="Price Override (optional)"
                            value={formData.priceOverride}
                            onChange={(e) => setFormData({ ...formData, priceOverride: e.target.value })}
                            helperText="Leave blank to use type default"
                            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Notes"
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            multiline
                            rows={2}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained">
                    {session ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Enrollment Dialog Component
interface EnrollmentDialogProps {
    open: boolean;
    onClose: () => void;
    session: PTSession | null;
    memberProfiles: MemberProfile[];
    onEnroll: (sessionId: string, memberProfileId: string, notes?: string) => Promise<{ success: boolean; error?: string }>;
    onCancel: (sessionId: string, enrollmentId: string) => void;
}

const EnrollmentDialog: React.FC<EnrollmentDialogProps> = ({ open, onClose, session, memberProfiles, onEnroll, onCancel }) => {
    const [selectedMember, setSelectedMember] = useState<MemberProfile | null>(null);
    const [enrollmentNotes, setEnrollmentNotes] = useState('');
    const [enrolling, setEnrolling] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (open) {
            setSelectedMember(null);
            setEnrollmentNotes('');
            setDialogError(null);
        }
    }, [open]);

    const handleEnroll = async () => {
        if (session && selectedMember) {
            setEnrolling(true);
            setDialogError(null);
            const result = await onEnroll(session.id, selectedMember.id, enrollmentNotes);
            setEnrolling(false);
            
            if (result.success) {
                setSelectedMember(null);
                setEnrollmentNotes('');
            } else {
                setDialogError(result.error || 'Failed to enroll member');
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Manage Enrollments - {session?.name}</DialogTitle>
            <DialogContent>
                {/* Error Message */}
                {dialogError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
                        {dialogError}
                    </Alert>
                )}
                
                {/* Current Enrollments */}
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Current Enrollments:
                </Typography>
                <List dense>
                    {session?.enrollments && session.enrollments.length > 0 ? (
                        session.enrollments.map((enrollment) => {
                            const isEnrolled = enrollment.status === 'enrolled';
                            const paymentMethod = enrollment.usedPackId ? 'Paid with Pack' : 'Charged';
                            const statusText = isEnrolled ? paymentMethod : enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1);
                            
                            return (
                                <ListItem key={enrollment.id} sx={{ opacity: isEnrolled ? 1 : 0.6 }}>
                                    <ListItemText
                                        primary={enrollment.memberName}
                                        secondary={statusText}
                                    />
                                    {isEnrolled && (
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                onClick={() => session && onCancel(session.id, enrollment.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    )}
                                </ListItem>
                            );
                        })
                    ) : (
                        <Typography variant="body2" color="text.secondary">No enrollments yet</Typography>
                    )}
                </List>

                {/* Add New Enrollment */}
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
                    Add New Enrollment:
                </Typography>
                <Autocomplete
                    options={memberProfiles}
                    value={selectedMember}
                    onChange={(event, newValue) => setSelectedMember(newValue)}
                    getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                    getOptionKey={(option) => option.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Member"
                            margin="normal"
                            placeholder="Search members..."
                        />
                    )}
                />
                <TextField
                    fullWidth
                    label="Notes (optional)"
                    value={enrollmentNotes}
                    onChange={(e) => setEnrollmentNotes(e.target.value)}
                    multiline
                    rows={2}
                    margin="normal"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={enrolling}>Close</Button>
                <Button 
                    onClick={handleEnroll} 
                    variant="contained" 
                    disabled={!selectedMember || enrolling}
                >
                    {enrolling ? 'Enrolling...' : 'Enroll Member'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PersonalTrainingsPage;
